import React, { useState } from 'react'
import { LayersTree } from '../LayersTree'
import * as S from './CanvasLeftPanel.styles'
import { useForTablet, ResizeHandle, Tooltip } from '@sketch/components'
import { useCanvasLeftPanelResizing } from './CanvasLeftPanelResizingContext'

type CanvasLeftPanelProps = {
  isInspectPanelOpen: boolean
  navigationComponent: React.ReactNode
}

/**
 * Floating panel on the left of the canvas containing the small page/frames selector
 * and, if in inspect mode, the layers tree.
 */
export function CanvasLeftPanel({
  isInspectPanelOpen,
  navigationComponent: selectorComponent,
}: CanvasLeftPanelProps) {
  const [hideLayersTree, setHideLayersTree] = useState(false)
  const isTabletAndBigger = useForTablet()

  /** true if the layers tree feature is enabled, could still be hidden */
  const hasLayersTree = isInspectPanelOpen && isTabletAndBigger

  const { width, isResizing, resizeHandleRef } = useCanvasLeftPanelResizing()

  return (
    <S.CanvasLeftPanel
      style={hasLayersTree ? { width } : undefined}
      $isResizing={isResizing}
      data-testid="canvas-left-panel"
    >
      {hasLayersTree && <ResizeHandle ref={resizeHandleRef} />}
      <S.CanvasLeftPanelHeader $isResizing={isResizing}>
        <S.SelectorContainer>{selectorComponent}</S.SelectorContainer>
        {hasLayersTree && (
          <S.ToggleLayersTreeContainer>
            <S.ToggleLayersTreeButtonDivider />
            <Tooltip
              content={hideLayersTree ? 'Show Layer List' : 'Hide Layer List'}
              placement="bottom"
              disableWhenTouchDevice
            >
              <S.ToggleLayersTreeButton
                $isActive={!hideLayersTree}
                onClick={() => setHideLayersTree(prev => !prev)}
                aria-label={
                  hideLayersTree ? 'Show Layer List' : 'Hide Layer List'
                }
              >
                <S.ToggleLayersTreeButtonIcon />
              </S.ToggleLayersTreeButton>
            </Tooltip>
          </S.ToggleLayersTreeContainer>
        )}
      </S.CanvasLeftPanelHeader>
      {hasLayersTree && !hideLayersTree && (
        <S.LayersTreeContainer $isResizing={isResizing}>
          <LayersTree />
        </S.LayersTreeContainer>
      )}
    </S.CanvasLeftPanel>
  )
}
